import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useApi, useAuth } from "@/contexts/AuthContext";

const HEIGHT_VAR = "--impersonation-banner-height";

// Shown on every screen while an admin is inside another user's session.
// z-[45]: above the admin sidebar (z-40), below dialogs (z-50), so it never
// covers a dialog's buttons.
export default function ImpersonationBanner() {
  const { user } = useAuth();
  const api = useApi();
  const [returning, setReturning] = useState(false);
  const bannerRef = useRef(null);
  const active = Boolean(user?.impersonating);

  // The page and the sidebar leave room for the banner's real height: a phone
  // wraps the text onto two lines, and the sidebar's logout button sits at the bottom.
  useEffect(() => {
    if (!active) return undefined;
    const root = document.documentElement;
    const reserveHeight = () => root.style.setProperty(HEIGHT_VAR, `${bannerRef.current?.offsetHeight || 0}px`);
    reserveHeight();
    window.addEventListener("resize", reserveHeight);
    return () => {
      window.removeEventListener("resize", reserveHeight);
      root.style.removeProperty(HEIGHT_VAR);
    };
  }, [active]);

  if (!active) return null;

  const returnToOwnUser = async () => {
    setReturning(true);
    try {
      await api.post("/auth/stop-impersonating");
      // Full page load, for the same reason as UserManager.handleImpersonate:
      // nothing fetched as the other user may stay on screen.
      try { window.sessionStorage.removeItem("adminWorkState"); } catch { /* reload still refetches */ }
      window.location.replace("/admin");
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בחזרה למשתמש שלך");
      setReturning(false);
    }
  };

  return (
    <div
      ref={bannerRef}
      dir="rtl"
      data-testid="impersonation-banner"
      className="fixed bottom-0 inset-x-0 z-[45] bg-amber-700 text-white text-sm text-center px-4 py-2.5 shadow-[0_-4px_12px_rgb(0,0,0,0.2)] print:hidden"
    >
      <span>הנך מחובר למערכת כ-<bdi className="font-bold">{user.name || user.email}</bdi>. </span>
      <button
        type="button"
        data-testid="impersonation-return"
        onClick={returnToOwnUser}
        disabled={returning}
        className="font-bold underline underline-offset-2 hover:text-amber-100 disabled:opacity-70"
      >
        לחץ כאן לחזור למשתמש שלך
      </button>
    </div>
  );
}
